import React, { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Badge,
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  DropdownV3 as Dropdown,
  Tooltip,
  CampaignAttributes,
} from 'portal-commons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircle,
  faCircleCheck,
  faSquare,
  faSquareCheck,
  faRoute,
  faChartNetwork,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { ContentBlockHeader, Divider, RadioButton } from '@/components';
import { ProgramRegistrationFormData } from './types';
import { useRoutingController } from './hooks';
import TwoColumnGrid from './TwoColumnGrid';
import { SelectItem } from '@/utils/types';

interface Props {
  onNext?: () => void;
  onPrevious?: () => void;
  cnpOptions: SelectItem[];
}

const StepRouting: FC<Props> = ({ onNext, onPrevious, cnpOptions }) => {
  const { control, watch } = useFormContext<ProgramRegistrationFormData>();
  const {
    routingMode,
    setRoutingMode,
    offNetOutbound,
    setOffNetOutbound,
    offNetOutboundError,
    intlInbound,
    setIntlInbound,
    intlInboundError,
    intlOutbound,
    setIntlOutbound,
    intlOutboundError,
    onNetInbound,
    setOnNetInbound,
    onNetInboundError,
    offNetInbound,
    setOffNetInbound,
    offNetInboundError,
    upstreamCnpUid,
    setUpstreamCnpUid,
    upstreamCnpUidError,
    mnoIds,
    setMnoIds,
    mnoIdsError,
  } = useRoutingController(control);

  // Watch global form state values
  const brandName = watch('usecase.brandName');
  const usecase = watch('ui.usecaseDisplayName');
  const mnos = watch('ui.mnoNetworks') || [];

  const handleSelectRoutingMode = (mode: 'COMMON' | 'DEDICATED') => {
    setRoutingMode(mode);
    if (mode === 'COMMON') {
      setOffNetOutbound(true);
    }
  };

  const handleToggleMno = (id: string) => {
    const currentMnoIds = mnoIds || [];
    if (currentMnoIds.includes(id)) {
      setMnoIds(currentMnoIds.filter((m) => m !== id));
    } else {
      setMnoIds([...currentMnoIds, id]);
    }
  };

  const isNextEnabled =
    !!routingMode &&
    [
      offNetOutbound,
      intlInbound,
      intlOutbound,
      onNetInbound,
      offNetInbound,
    ].every((val) => val !== undefined) &&
    !!upstreamCnpUid &&
    (mnoIds?.length ?? 0) > 0;

  return (
    <Flex
      sx={{ flexDirection: 'column', gap: 'xl', width: '100%' }}
      data-testid="stepRouting"
    >
      <ContentBlockHeader
        title="Register Messaging Program"
        subTitle="Step 2 of 3 — Configure network routing and upstream partners."
        sx={{ mb: 0 }}
      />
      <Divider />

      {/* Top capsule Badges */}
      <Flex sx={{ gap: '16px', alignItems: 'center' }}>
        <Badge type="neutral" size="lg" variant="fill" title="Brand Name:">
          {brandName}
        </Badge>
        <Badge
          type="neutral"
          size="lg"
          variant="fill"
          title="Use case"
          sx={{ flexShrink: 0 }}
        >
          {usecase}
        </Badge>
      </Flex>

      <Box sx={{ fontSize: '14px', color: '#4d5659', fontWeight: 400 }}>
        * Required Fields
      </Box>

      {/* SECTION 1: Routing Mode */}
      <ContentBlockHeader
        title="Routing Mode"
        subTitle="Choose how Sender IDs are assigned across carrier networks*"
        icon={faRoute}
        sx={{ mb: 0 }}
      />

      {/* Routing Cards Grid */}
      <TwoColumnGrid sx={{ width: '100%' }}>
        {/* Card 1: Common Sender ID */}
        <RoutingCard
          title="Common Sender ID"
          description="Use a single Sender ID across multiple networks through one primary connection."
          selected={routingMode === 'COMMON'}
          onClick={() => handleSelectRoutingMode('COMMON')}
          testId="routingCard-COMMON"
        />

        {/* Card 2: Dedicated Sender ID */}
        <RoutingCard
          title="Dedicated Sender ID"
          description="Assign separate unique Sender IDs for each individual carrier network."
          selected={routingMode === 'DEDICATED'}
          onClick={() => handleSelectRoutingMode('DEDICATED')}
          testId="routingCard-DEDICATED"
        />
      </TwoColumnGrid>

      {/* SECTION 2: Select Sender ID Supporting Preferences (Dynamic Expand) */}
      {!!routingMode && (
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '8px',
            '& [data-testid="campaignAttributeNewValue"]': {
              justifyContent: 'flex-end',
            },
          }}
        >
          <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#4d5659' }}>
            Select Sender ID Supporting Preferences
          </Box>

          {/* Pref 1: Off-net outbound */}
          <CampaignAttributes.Attribute
            id="offNetOutbound"
            label="Off-net outbound traffic termination*"
            tooltip="Enable this if your outbound messages need to reach end-users who are on domestic mobile networks outside your direct carrier's network."
            value={offNetOutbound}
            onChange={setOffNetOutbound}
            disabledOption={routingMode === 'COMMON' ? 'no' : undefined}
            data-testid="option-offNetOutbound"
            error={
              offNetOutboundError ? offNetOutboundError?.message : undefined
            }
          />

          {/* Pref 2: International inbound */}
          <CampaignAttributes.Attribute
            id="intlInbound"
            label="International inbound termination for A2P SMS*"
            tooltip="Enable this if you expect to receive incoming messages or replies from end-users with non-domestic (international) phone numbers."
            value={intlInbound}
            onChange={setIntlInbound}
            data-testid="option-intlInbound"
            error={intlInboundError ? intlInboundError?.message : undefined}
          />

          {/* Pref 3: International outbound */}
          <CampaignAttributes.Attribute
            label={
              <Box>
                International outbound termination for A2P SMS
                <Box>(termination to international off-net numbers)*</Box>
              </Box>
            }
            tooltip="Enable this if you plan to send outbound messages to international phone numbers routed across foreign telecom networks."
            value={intlOutbound}
            onChange={setIntlOutbound}
            data-testid="option-intlOutbound"
            error={intlOutboundError ? intlOutboundError?.message : undefined}
          />

          {/* Pref 4: On-net inbound */}
          <CampaignAttributes.Attribute
            label="On-net inbound traffic termination*"
            tooltip="Enable this to receive inbound messages (2-way replies) from end-users on your primary connected carrier network."
            value={onNetInbound}
            onChange={setOnNetInbound}
            data-testid="option-onNetInbound"
            error={onNetInboundError ? onNetInboundError?.message : undefined}
          />

          {/* Pref 5: Off-net inbound */}
          <CampaignAttributes.Attribute
            label="Off-net inbound traffic termination*"
            tooltip="Enable this to receive inbound messages (2-way replies) from end-users on other domestic mobile networks."
            value={offNetInbound}
            onChange={setOffNetInbound}
            data-testid="option-offNetInbound"
            error={offNetInboundError ? offNetInboundError?.message : undefined}
          />
        </Flex>
      )}

      <Divider />

      {/* SECTION 3: Connectivity Path */}
      <ContentBlockHeader
        title="Connectivity Path"
        icon={faChartNetwork}
        sx={{ mb: 0 }}
      />

      <Flex
        sx={{ width: 'calc(50% - 12px)', alignItems: 'center', gap: '4px' }}
      >
        <Dropdown
          label="Select Your Connectivity Partner*"
          options={cnpOptions}
          value={upstreamCnpUid}
          onChange={(val) => setUpstreamCnpUid(val)}
          error={!!upstreamCnpUidError}
          note={upstreamCnpUidError?.message}
          data-testid="cnpListDropdown"
        />
        <Tooltip title="Select the approved Connectivity Network Partner (CNP) or aggregator responsible for routing this Sender ID's traffic to carriers.">
          <FontAwesomeIcon
            icon={faInfoCircle}
            style={{ fontSize: '16px', cursor: 'pointer', marginLeft: '8px' }}
          />
        </Tooltip>
      </Flex>

      {/* SECTION 4: MNO Networks */}
      <Flex
        sx={{
          flexDirection: 'column',
          gap: 'xs',
          fontSize: '14px',
          color: '#4d5659',
        }}
      >
        <Box sx={{ fontWeight: 600 }}>MNO Networks</Box>
        <div>Select one or more MNOs to target for messaging traffic.</div>

        {/* MNO Checkboxes Container */}
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '24px',
            border: '1px solid',
            borderColor: mnoIdsError?.message ? '#E03E2D' : '#CCCFD0',
            borderRadius: '8px',
            padding: '12px 16px',
            position: 'relative',
          }}
        >
          {!!mnoIdsError && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                color: '#E03E2D',
                fontSize: '10px',
              }}
            >
              {mnoIdsError.message}
            </Box>
          )}
          {mnos.map((mno) => (
            <NetworkOption
              key={mno.id}
              label={mno.name}
              value={mno.id}
              checked={mnoIds.includes(mno.id)}
              disabled={mno.disabled}
              onChange={handleToggleMno}
              testId={`mnoNetwork-${mno.id}`}
            />
          ))}
        </Flex>
      </Flex>

      <Divider />

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
        <Button
          type="button"
          shape="square"
          color="primary"
          variant="outline"
          size="lg"
          onClick={onPrevious}
          data-testid="prevStepBtn"
        >
          Previous
        </Button>
        <Button
          type="button"
          shape="square"
          color="primary"
          size="lg"
          disabled={!isNextEnabled}
          onClick={onNext}
          data-testid="nextStepBtn"
        >
          Next: Program Details
        </Button>
      </Box>
    </Flex>
  );
};

const RoutingCard: FC<{
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
  testId?: string;
}> = ({ title, description, selected, onClick, testId }) => {
  return (
    <Box
      onClick={onClick}
      data-testid={testId}
      sx={{
        border: selected ? '2px solid #0091B3' : '1px solid #CCCFD0',
        borderRadius: '8px',
        p: '16px',
        cursor: 'pointer',
        display: 'flex',
        gap: '16px',
      }}
    >
      <FontAwesomeIcon
        icon={selected ? faCircleCheck : faCircle}
        color={selected ? '#0091B3' : '#333e41'}
        style={{ fontSize: '18px', marginTop: '1px' }}
      />
      <Flex sx={{ flexDirection: 'column', gap: '8px' }}>
        <Box sx={{ fontWeight: 600, fontSize: '16px', color: '#19262a' }}>
          {title}
        </Box>
        <Box sx={{ fontSize: '14px', color: '#4d5659', lineHeight: 'normal' }}>
          {description}
        </Box>
      </Flex>
    </Box>
  );
};

const PreferenceOption: FC<{
  label: React.ReactNode;
  tooltip: string;
  value?: boolean;
  onChange: (val: boolean) => void;
  disableNo?: boolean;
  testId?: string;
}> = ({ label, tooltip, value, onChange, disableNo, testId }) => {
  return (
    <Flex
      data-testid={testId}
      sx={{
        backgroundColor: '#f5f9fb',
        p: '7px',
        height: 44,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      {/* Label and Tooltip wrapper */}
      <Flex sx={{ alignItems: 'center', p: 8, gap: 8, color: '#0091b3' }}>
        <Box sx={{ fontSize: '12px', fontWeight: 600, lineHeight: 'normal' }}>
          {label}
        </Box>
        {tooltip && (
          <Tooltip title={tooltip}>
            <FontAwesomeIcon
              icon={faInfo}
              style={{ fontSize: '12px', fontWeight: 900, cursor: 'pointer' }}
            />
          </Tooltip>
        )}
      </Flex>

      <Flex sx={{ gap: '16px', alignItems: 'center' }}>
        <RadioButton
          label="Yes"
          value="yes"
          data-testid="radioYes"
          checked={value === true}
          onChange={() => onChange(true)}
        />
        <RadioButton
          label="No"
          value="no"
          data-testid="radioNo"
          checked={value === false}
          disabled={disableNo}
          onChange={() => onChange(false)}
        />
      </Flex>
    </Flex>
  );
};

const NetworkOption: FC<{
  label: string;
  value: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (val: string) => void;
  testId?: string;
}> = ({ label, value, checked, disabled, onChange, testId }) => {
  return (
    <Flex
      onClick={() => !disabled && onChange(value)}
      data-testid={testId}
      sx={{
        alignItems: 'center',
        gap: '16px',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <FontAwesomeIcon
        icon={checked ? faSquareCheck : faSquare}
        color={disabled ? '#bcc1c3' : checked ? '#0091B3' : '#808789'}
        style={{ fontSize: '16px' }}
      />
      <Box
        sx={{
          ml: 16,
          fontSize: '12px',
          color: disabled ? '#bcc1c3' : '#4d5659',
        }}
      >
        {label}
      </Box>
    </Flex>
  );
};

export default StepRouting;
